import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader,
  AlertCircle,
  Tag,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  PartyPopper,
  Briefcase,
} from 'lucide-react';
import { eventTeamApiService, EventTeamApiError } from '../../../services/eventTeamApi';
import type { EventTag } from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { EventTagChip } from '../EventTagChip';
import { ConfirmationModal } from '../../ConfirmationModal';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

export interface EventTagsPanelProps {
  perms: EventTeamPerms;
}

/**
 * Tag Manager (admin — guide §1.12): CRUD with 409 duplicate-name handling and
 * per-tag drill-down into tagged events/projects.
 */
export const EventTagsPanel = ({ perms }: EventTagsPanelProps) => {
  const navigate = useNavigate();
  const [tags, setTags] = useState<EventTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#22c55e');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#22c55e');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [tagToDelete, setTagToDelete] = useState<EventTag | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [targets, setTargets] = useState<{ events: string[]; projects: string[] } | null>(null);
  const [targetsLoading, setTargetsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventTeamApiService.listTags();
      setTags(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      await eventTeamApiService.createTag({
        name: newName.trim(),
        color: newColor,
        description: newDescription.trim() || null,
      });
      setNewName('');
      setNewColor('#22c55e');
      setNewDescription('');
      setShowCreate(false);
      await load();
    } catch (err) {
      if (err instanceof EventTeamApiError && err.status === 409) {
        setCreateError(`A tag named "${newName.trim()}" already exists.`);
      } else {
        setCreateError(err instanceof Error ? err.message : 'Failed to create tag');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async (tag: EventTag) => {
    if (!editName.trim()) return;
    setSavingEdit(true);
    try {
      await eventTeamApiService.updateTag(tag.id, {
        name: editName.trim(),
        color: editColor,
        description: editDescription.trim() || null,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      if (err instanceof EventTeamApiError && err.status === 409) {
        alert(`A tag named "${editName.trim()}" already exists.`);
      } else {
        alert(err instanceof Error ? err.message : 'Failed to update tag');
      }
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!tagToDelete) return;
    setDeleting(true);
    try {
      await eventTeamApiService.deleteTag(tagToDelete.id);
      setTagToDelete(null);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete tag');
    } finally {
      setDeleting(false);
    }
  };

  const toggleTargets = async (tag: EventTag) => {
    if (expandedId === tag.id) {
      setExpandedId(null);
      setTargets(null);
      return;
    }
    setExpandedId(tag.id);
    setTargets(null);
    setTargetsLoading(true);
    try {
      const res = await eventTeamApiService.getTagTargets(tag.id);
      setTargets(res);
    } catch {
      setTargets({ events: [], projects: [] });
    } finally {
      setTargetsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Tag Manager"
        icon={Tag}
        count={tags.length}
        description="Tags shared by events and projects. Expand a tag to see what uses it."
        className="mb-0 flex-wrap"
        actions={
          perms.canAdmin ? (
            <button
              type="button"
              onClick={() => setShowCreate(!showCreate)}
              className="glass-button"
              aria-expanded={showCreate}
            >
              <Plus className="w-4 h-4" />
              New tag
            </button>
          ) : undefined
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {showCreate && perms.canAdmin && (
        <div className="glass-card p-3 md:p-4 space-y-3">
          {createError && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{createError}</span>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Tag name"
              className="glass-input text-sm w-48"
              autoFocus
            />
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="glass-input h-[38px] w-16 p-1 cursor-pointer"
              title="#RRGGBB color"
            />
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Description (optional)"
              className="glass-input text-sm flex-1 min-w-[180px]"
            />
            <button type="button" onClick={() => setShowCreate(false)} className="glass-button-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating || !newName.trim()}
              className="btn-primary"
            >
              {creating ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="glass-card p-3 flex items-center gap-3">
              <SkeletonLoader className="h-4 w-4" />
              <SkeletonLoader className="h-5 w-24 rounded-full" />
              <SkeletonLoader className="h-3 w-48 max-w-full" />
            </div>
          ))}
        </div>
      ) : tags.length === 0 && !error ? (
        <EmptyState
          icon={Tag}
          title="No tags"
          description={perms.canAdmin ? 'Create a tag to start organising events and projects.' : undefined}
        />
      ) : (
        <div className="space-y-2">
          {tags.map((tag) => {
            const isEditing = editingId === tag.id;
            const expanded = expandedId === tag.id;
            return (
              <div key={tag.id} className="glass-card overflow-visible">
                {isEditing ? (
                  <div className="p-3 flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="glass-input text-sm w-44"
                    />
                    <input
                      type="color"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="glass-input h-[38px] w-16 p-1 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description"
                      className="glass-input text-sm flex-1 min-w-[160px]"
                    />
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="btn-icon btn-icon-sm"
                      title="Cancel"
                      aria-label="Cancel editing"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleSaveEdit(tag)}
                      disabled={savingEdit || !editName.trim()}
                      className="btn-icon btn-icon-sm text-green-400"
                      title="Save"
                      aria-label="Save tag"
                    >
                      {savingEdit ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 p-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => void toggleTargets(tag)}
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      aria-expanded={expanded}
                    >
                      {expanded ? (
                        <ChevronDown className="w-4 h-4 text-glass-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-glass-400 flex-shrink-0" />
                      )}
                      <EventTagChip tag={tag} />
                      {tag.description && (
                        <span className="text-xs text-glass-500 italic truncate">{tag.description}</span>
                      )}
                    </button>
                    {perms.canAdmin && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => {
                            setEditingId(tag.id);
                            setEditName(tag.name);
                            setEditColor(tag.color);
                            setEditDescription(tag.description ?? '');
                          }}
                          className="btn-icon btn-icon-sm"
                          title="Edit tag"
                          aria-label="Edit tag"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setTagToDelete(tag)}
                          className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                          title="Delete tag"
                          aria-label="Delete tag"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {expanded && !isEditing && (
                  <div className="px-3 pb-3 border-t border-glass-200/10 pt-2">
                    {targetsLoading ? (
                      <Loader className="w-4 h-4 animate-spin text-glass-400" />
                    ) : !targets || (targets.events.length === 0 && targets.projects.length === 0) ? (
                      <p className="text-xs text-glass-500">Nothing is tagged with "{tag.name}".</p>
                    ) : (
                      <div className="space-y-2">
                        {targets.events.length > 0 && (
                          <div>
                            <p className="eyebrow mb-1">
                              Events ({targets.events.length})
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {targets.events.map((eventId) => (
                                <button
                                  key={eventId}
                                  onClick={() => navigate(`/event-team/events/${eventId}`)}
                                  className="chip font-mono hover:text-white hover:border-glass-200/40 transition-colors"
                                >
                                  <PartyPopper className="w-3 h-3 text-accent-400" />
                                  {eventId.slice(0, 8)}…
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {targets.projects.length > 0 && (
                          <div>
                            <p className="eyebrow mb-1">
                              Projects ({targets.projects.length})
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {targets.projects.map((projectId) => (
                                <button
                                  key={projectId}
                                  onClick={() => navigate(`/event-team/projects/${projectId}`)}
                                  className="chip font-mono hover:text-white hover:border-glass-200/40 transition-colors"
                                >
                                  <Briefcase className="w-3 h-3 text-blue-400" />
                                  {projectId.slice(0, 8)}…
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tagToDelete && (
        <ConfirmationModal
          isOpen={!!tagToDelete}
          onClose={() => setTagToDelete(null)}
          onConfirm={() => void handleDelete()}
          title="Delete Tag"
          message={`Delete the tag "${tagToDelete.name}"? It will be removed from every tagged event and project.`}
          confirmText="Delete"
          isLoading={deleting}
        />
      )}
    </div>
  );
};
